<script setup lang="ts">
/**
 * MaterialList —— 详情页附件下载区（F19）。
 *
 * 重构后的附件区实现成果详情页附件的 PDF.js inline 直接嵌入展示。
 * 1. 非 PDF 材料在内嵌区上方独立列出（简单的卡片列表，仅保留文件名、类型、大小和下载按钮）。
 * 2. PDF 材料直接在下方内嵌展示：
 *    - 若只有一个 PDF：占满容器宽度的滚动 viewer，不渲染左侧侧栏。
 *    - 若有两个或以上 PDF：宽屏时左侧切换侧栏 + 右侧 viewer，窄内容区自动把切换栏堆到上方。
 * 3. 若无 PDF 材料，只显示非 PDF 下载区；若空附件，展示空态。
 */
import { computed, ref, watch } from 'vue';
import { copy } from '@/i18n/zh-CN';
import { formatBytes, hasText } from '@/registry/sections/section-presenters';
import type { MaterialView } from '@/api/types';
import PdfBlobFrame from './PdfBlobFrame.vue';

const PDF_CONTENT_TYPE = 'application/pdf';

const props = defineProps<{
  readonly materials: readonly MaterialView[];
  readonly title?: string;
  readonly elementId?: string;
  readonly previewToken?: string | null;
}>();

const list = computed(() => props.materials);
const titleText = computed(() => props.title ?? copy.detail.materialTitle);

function isPdf(material: MaterialView): boolean {
  return material.contentType === PDF_CONTENT_TYPE;
}

const pdfMaterials = computed(() => list.value.filter(isPdf));
const nonPdfMaterials = computed(() => list.value.filter((m) => !isPdf(m)));

const defaultActivePdfId = computed(() => pdfMaterials.value[0]?.id ?? null);
const currentActivePdfId = ref<string | null>(null);

watch(
  defaultActivePdfId,
  (newId) => {
    currentActivePdfId.value = newId;
  },
  { immediate: true }
);

const activePdf = computed(() => {
  return pdfMaterials.value.find((p) => p.id === currentActivePdfId.value) ?? pdfMaterials.value[0] ?? null;
});

function downloadUrl(material: MaterialView): string {
  let url = `/api/public/materials/${material.id}/download`;
  if (props.previewToken) {
    url += `?token=${encodeURIComponent(props.previewToken)}`;
  }
  return url;
}
</script>

<template>
  <section
    :id="elementId"
    :class="$style.section"
    data-shell="detail-section"
    data-testid="detail-material"
  >
    <h2 :class="$style.title" data-shell="section-title">{{ titleText }}</h2>

    <div v-if="list.length === 0" :class="$style.empty" data-testid="detail-material-empty">
      {{ copy.sections.empty }}
    </div>

    <div v-else :class="$style.container">
      <div v-if="nonPdfMaterials.length > 0" :class="$style.nonPdfSection">
        <ul :class="$style.nonPdfList">
          <li
            v-for="material in nonPdfMaterials"
            :key="material.id"
            :class="$style.nonPdfItem"
            data-testid="non-pdf-item"
          >
            <div :class="$style.nonPdfInfo">
              <h3 :class="$style.nonPdfName">{{ material.fileName }}</h3>
              <p :class="$style.nonPdfMeta">
                <span>{{ hasText(material.contentType) ? material.contentType : copy.sections.material.typeUnknown }}</span>
                <span>{{ formatBytes(material.sizeBytes) || copy.sections.material.sizeUnknown }}</span>
              </p>
            </div>
            <div :class="$style.nonPdfActions">
              <a
                :class="$style.downloadBtn"
                :href="downloadUrl(material)"
                :download="material.fileName"
                :data-testid="`material-download-${material.id}`"
              >
                {{ copy.buttons.download }}
              </a>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="pdfMaterials.length > 0" :class="$style.pdfSection" data-testid="pdf-preview-section">
        <div v-if="pdfMaterials.length >= 2" :class="$style.pdfPreviewContainer" data-testid="pdf-preview-multi">
          <div :class="$style.pdfSidebar" data-testid="pdf-sidebar">
            <button
              v-for="pdf in pdfMaterials"
              :key="pdf.id"
              type="button"
              :class="[
                $style.pdfSidebarItem,
                currentActivePdfId === pdf.id ? $style.pdfSidebarItemActive : ''
              ]"
              :data-testid="`pdf-sidebar-item-${pdf.id}`"
              :aria-current="currentActivePdfId === pdf.id ? 'true' : undefined"
              @click="currentActivePdfId = pdf.id"
            >
              {{ pdf.fileName }}
            </button>
          </div>
          <div :class="$style.iframeContainer">
            <PdfBlobFrame
              v-if="activePdf"
              :key="activePdf.id"
              :material-id="activePdf.id"
              :file-name="activePdf.fileName"
              :preview-token="props.previewToken"
              :cache-version="activePdf.updatedAt"
              :class="$style.iframe"
            />
          </div>
        </div>

        <div v-else :class="$style.iframeSingleContainer" data-testid="pdf-preview-single">
          <PdfBlobFrame
            v-if="activePdf"
            :material-id="activePdf.id"
            :file-name="activePdf.fileName"
            :preview-token="props.previewToken"
            :cache-version="activePdf.updatedAt"
            :class="$style.iframeSingle"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style module src="./MaterialList.module.css"></style>
